import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar, RefreshCw, FileText, CheckCircle2, ChevronUp, type LucideProps } from 'lucide-react';

// --- TYPE DEFINITIONS ---

export const ACTIVITY_VARIANTS = {
  LIST: 'list',
  DETAILS: 'details',
} as const;

type ActivityVariant = typeof ACTIVITY_VARIANTS[keyof typeof ACTIVITY_VARIANTS];

export interface Activity {
  icon: React.ComponentType<LucideProps>;
  action: string;
  description: string;
  patient: string;
  timestamp: string;
}

export interface ActivityDetailsData extends Activity {
  location: string;
  date: string;
  provider: string;
  op: string;
}

// --- SUB-COMPONENTS ---

interface ActivityCardProps {
  title: string;
  buttonText: string;
  onButtonClick?: () => void;
  children: React.ReactNode;
  isCollapsible?: boolean;
  className?: string;
}

const ActivityCard = React.forwardRef<HTMLDivElement, ActivityCardProps>((
  { title, buttonText, onButtonClick, children, isCollapsible = false, className = '' }, ref) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div ref={ref} className={cn('bg-white rounded-lg p-4 shadow-sm border border-gray-200 w-full font-sans', className)}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <Button 
            variant="link" 
            onClick={onButtonClick} 
            className="text-sky-600 hover:text-sky-700 p-0 h-auto text-sm font-medium"
          >
            {buttonText}
          </Button>
          {isCollapsible && (
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="bg-white border-sky-200 text-sky-600 hover:bg-sky-100 w-9 h-9"
            >
              <ChevronUp className={cn('transition-transform', isCollapsed && 'rotate-180')} />
            </Button>
          )}
        </div>
      </div>
      {!isCollapsed && <div className="space-y-4">{children}</div>}
    </div>
  );
});
ActivityCard.displayName = 'ActivityCard';

interface ActivityItemProps {
  activity: Activity;
}

const ActivityItem = React.forwardRef<HTMLDivElement, ActivityItemProps>(({ activity }, ref) => {
  const { icon: Icon, action, description, patient, timestamp } = activity;
  return (
    <div ref={ref} className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-gray-500" />
      </div>
      <div className="flex-grow min-w-0">
        <p className="text-sm text-gray-700">
          <span className="font-medium">{action}</span> {description} <span className="font-medium text-sky-700">{patient}</span>
        </p>
      </div>
      <p className="text-xs text-gray-500 whitespace-nowrap">{timestamp}</p>
    </div>
  );
});
ActivityItem.displayName = 'ActivityItem';

interface ActivityDetailsProps {
  data: ActivityDetailsData;
}

const ActivityDetails = React.forwardRef<HTMLDivElement, ActivityDetailsProps>(({ data }, ref) => {
  return (
    <div ref={ref} className="space-y-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Location</p>
          <p className="text-sm text-gray-900 font-medium">{data.location}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Date</p>
          <p className="text-sm text-gray-900 font-medium">{data.date}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Provider</p>
          <p className="text-sm text-gray-900 font-medium">{data.provider}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Op</p>
          <p className="text-sm text-gray-900 font-medium">{data.op}</p>
        </div>
      </div>
    </div>
  );
});
ActivityDetails.displayName = 'ActivityDetails';

// --- MAIN COMPONENT ---

export interface ActivityMenuProps {
  variant?: ActivityVariant;
  activities?: Activity[];
  activityDetails?: ActivityDetailsData;
  onButtonClick?: () => void;
  className?: string;
}

export const ActivityMenu = React.forwardRef<HTMLDivElement, ActivityMenuProps>((
  { 
    variant = ACTIVITY_VARIANTS.LIST, 
    activities = [],
    activityDetails,
    onButtonClick,
    className = ''
  }, ref) => {

    if (variant === ACTIVITY_VARIANTS.DETAILS) {
    if (!activityDetails) {
      console.warn('ActivityMenu: activityDetails object is required for details variant');
      return null;
    }
    return (
      <ActivityCard 
        ref={ref}
        title="Activity Details" 
        buttonText="Open Record"
        isCollapsible
        onButtonClick={onButtonClick}
        className={className}
      >
        <ActivityDetails data={activityDetails} />
      </ActivityCard>
    );
  }

  return (
    <ActivityCard 
      ref={ref}
      title="Recent Activities" 
      buttonText="View all"
      onButtonClick={onButtonClick}
      className={className}
    >
      {activities.map((activity, index) => (
        <ActivityItem key={`activity-${index}`} activity={activity} />
      ))}
    </ActivityCard>
  );
});

ActivityMenu.displayName = 'ActivityMenu';


